import React, { useState, useEffect } from 'react';
import { Grid, Card, Icon, IconButton, Typography, Modal, Box, TextField, Button } from '@mui/material';
import SoftBox from 'components/SoftBox';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useUpdateAgency, useUpdateAgencyCustomFieldOptions } from 'queries/agency';
import { useAgencyInfo } from 'context/agency';
import Swal from 'sweetalert2';
import Loader from 'examples/Loader';

const Sectors = () => {
    const queryClient = useQueryClient();
    const { mutateAsync: updateAgency } = useUpdateAgency(true);
    const { mutateAsync: updateAgencyCustomFieldOptions } = useUpdateAgencyCustomFieldOptions();
    const [agency, setAgencyUpdate] = useAgencyInfo();
    const [sectors, setSectors] = useState([]);
    const [openModal, setOpenModal] = useState(false);
    const [modalData, setModalData] = useState({ name: '', id: null });

    useEffect(() => {
        if (agency && agency.sectors) {
            let data = [...agency.sectors];
            // Ensure "Other" is always present if needed, though ideally it should be in the DB
            const hasOther = data.some(item => item.name.toLowerCase() === 'other');
            if (!hasOther) {
                data.push({ name: 'Other', id: 'other-id', isStatic: true });
            }
            setSectors(data);
        } else {
            setSectors([{ name: 'Other', id: 'other-id', isStatic: true }]);
        }
    }, [agency]);

    const handleOpenModal = (data = { name: '', id: null }) => {
        if (data.name.toLowerCase() === 'other') return;
        setModalData(data);
        setOpenModal(true);
    };

    const handleCloseModal = () => {
        setOpenModal(false);
        setModalData({ name: '', id: null });
    };

    const { mutateAsync: saveSectors, isPending: loading } = useMutation({
        mutationFn: async (updatedSectors) => {
            const { response } = await updateAgency({ _id: agency._id, sectors: updatedSectors });
            if (response && response.success) {
                await updateAgencyCustomFieldOptions({ agency_id: agency._id, options: [...updatedSectors?.map((item) => item.name)], field_id: agency?.custom_fields?.sector?.split('-')?.[0] });
            }
            return response;
        },
        onSuccess: (response) => {
            if (!response || !response.success) return;
            setAgencyUpdate(response.data);
            queryClient.invalidateQueries({ queryKey: ['agency'] });
        },
    });

    const handleSave = async () => {
        if (!modalData.name.trim()) return;


        const newName = modalData.name.trim().toLowerCase();
        const isDuplicate = sectors.some(s =>
            s.name.trim().toLowerCase() === newName &&
            (s.id || s._id) !== modalData.id
        );
        if (isDuplicate) {
            Swal.fire("Duplicate sector", "A sector with this name already exists.", "warning");
            return;
        }

        try {
            let updatedSectors = [...sectors];
            // Remove the hardcoded static Other before saving if it has our temporary ID
            updatedSectors = updatedSectors.filter(s => s.id !== 'other-id');

            if (modalData.id) {
                // Edit existing
                updatedSectors = updatedSectors.map(s =>
                    s.id === modalData.id ? { ...s, name: modalData.name } : s
                );
            } else {
                // Add new - backend will handle ID generation if we just send name
                updatedSectors.push({ name: modalData.name });
            }

            const response = await saveSectors(updatedSectors);
            if (response && response.success) {
                handleCloseModal();
            }
        } catch (error) {
            console.error("Error saving sector:", error);
        }
    };

    const handleDelete = (id, name) => {
        if (name.toLowerCase() === 'other') return;
        Swal.fire({
            title: 'Are you sure?',
            text: "You won't be able to revert this!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#344767',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, delete it!'
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    let updatedSectors = sectors.filter(s => s.id !== id && s.id !== 'other-id');
                    await saveSectors(updatedSectors);
                } catch (error) {
                    console.error("Error deleting sector:", error);
                }
            }
        });
    };

    if (loading) return <Loader />;

    return (
        <SoftBox p={3}>
            <Grid container style={{ gap: '8px' }}>
                {sectors.map((sector) => {
                    const isOther = sector.name.toLowerCase() === 'other';
                    return (
                        <Grid item xs={12} sm={6} md={4} lg={3} key={sector.id || sector._id}>
                            <Card sx={{
                                p: 2,
                                display: 'flex',
                                flexDirection: 'row',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                backgroundColor: isOther ? '#fff' : '#fff',
                                border: '1px solid #ebebeb',
                                borderRadius: '12px',
                                boxShadow: 'none',
                                minHeight: '60px',
                                paddingLeft: '10px'
                            }}>
                                <Typography variant="button" fontWeight="medium" sx={{ color: '#344767', textTransform: 'none', fontSize: '14px' }}>
                                    {sector.name}
                                </Typography>
                                {!isOther && (
                                    <SoftBox display="flex" alignItems="center" sx={{ gap: 1 }}>
                                        <IconButton size="small" onClick={() => handleOpenModal(sector)}>
                                            <Icon sx={{ color: '#000', fontSize: '18px !important' }}>edit</Icon>
                                        </IconButton>
                                        <IconButton size="small" onClick={() => handleDelete(sector.id || sector._id, sector.name)}>
                                            <Icon sx={{ color: '#f44336', fontSize: '18px !important' }}>delete</Icon>
                                        </IconButton>
                                    </SoftBox>
                                )}
                            </Card>
                        </Grid>
                    );
                })}
            </Grid>
            <SoftBox mt={4}>
                <Button
                    variant="outlined"
                    onClick={() => handleOpenModal()}
                    sx={{
                        border: '1px solid #ebebeb',
                        color: '#000',
                        textTransform: 'none',
                        borderRadius: '8px',
                        px: 3,
                        py: 1,
                        fontSize: '14px',
                        fontWeight: 'bold',
                        '&:hover': {
                            border: '1px solid #000',
                            backgroundColor: 'transparent'
                        }
                    }}
                >
                    + Add New Sector
                </Button>
            </SoftBox>

            <Modal open={openModal} onClose={handleCloseModal}>
                <Box sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: 450,
                    bgcolor: '#fff',
                    borderRadius: '16px',
                    boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
                    p: 4,
                }}>
                    <SoftBox display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                        <Typography variant="h6" fontWeight="bold">{modalData.id ? 'Edit sector' : 'Create new sector'}</Typography>
                        <Icon sx={{ cursor: 'pointer', color: '#000' }} onClick={handleCloseModal}>close</Icon>
                    </SoftBox>
                    <TextField
                        fullWidth
                        placeholder="Add sector"
                        value={modalData.name}
                        onChange={(e) => setModalData({ ...modalData, name: e.target.value })}
                        variant="outlined"
                        sx={{
                            mb: 3,
                            '& .MuiOutlinedInput-root': {
                                borderRadius: '8px',
                            }
                        }}
                    />
                    <Button
                        fullWidth
                        variant="contained"
                        onClick={handleSave}
                        disabled={loading}
                        sx={{
                            backgroundColor: '#000 !important',
                            color: '#fff',
                            borderRadius: '8px',
                            py: 1.5,
                            textTransform: 'none',
                            fontSize: '16px',
                            fontWeight: 'bold'
                        }}
                    >
                        {loading ? "Saving..." : "Save"}
                    </Button>
                </Box>
            </Modal>
        </SoftBox>
    );
};

export default Sectors;